/**
 * CoinFly — visual feedback when the child claims coins. A coin emoji
 * flies from a start point (usually the claim button) to the wallet pill
 * in the TopBar, then fades.
 *
 * Trigger by dispatching `xpb:coin-fly` on `window` with
 *   detail: { startX, startY, amount, source? }
 * where startX/startY are page-relative pixel coords (e.g. a button's
 * getBoundingClientRect() center). The component finds the wallet pill via
 * `[data-coin-target]` and animates between the two.
 */
import { useEffect, useState } from 'react';

interface Flying {
  id: number;
  left: number;
  top: number;
  dx: number;
  dy: number;
  delay: number;
}

export default function CoinFly() {
  const [coins, setCoins] = useState<Flying[]>([]);

  useEffect(() => {
    function onFly(e: Event) {
      const detail = (e as CustomEvent<{ startX?: number; startY?: number; amount?: number }>).detail || {};
      const target = document.querySelector<HTMLElement>('[data-coin-target]');
      if (!target) return;
      const rect = target.getBoundingClientRect();
      const endX = rect.left + rect.width / 2;
      const endY = rect.top + rect.height / 2;
      const startX = Number(detail.startX) || endX;
      const startY = Number(detail.startY) || endY;
      const count = Math.max(1, Math.min(8, Math.ceil((Number(detail.amount) || 100) / 50)));
      const baseId = Date.now();
      const next: Flying[] = [];
      for (let i = 0; i < count; i++) {
        const left = startX + (Math.random() * 28 - 14);
        const top = startY + (Math.random() * 16 - 8);
        next.push({
          id: baseId + i,
          left,
          top,
          dx: endX - left,
          dy: endY - top,
          delay: i * 60,
        });
      }
      setCoins((prev) => [...prev, ...next]);
      window.setTimeout(() => {
        setCoins((prev) => prev.filter((c) => !next.some((n) => n.id === c.id)));
      }, 1100 + count * 60);
    }
    window.addEventListener('xpb:coin-fly', onFly);
    return () => window.removeEventListener('xpb:coin-fly', onFly);
  }, []);

  if (coins.length === 0) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 90,
      }}
    >
      {coins.map((c) => (
        <span
          key={c.id}
          className="xpb-coin-fly"
          style={{
            left: c.left,
            top: c.top,
            ['--coin-dx' as string]: `${c.dx}px`,
            ['--coin-dy' as string]: `${c.dy}px`,
            animationDelay: `${c.delay}ms`,
          }}
        >
          🪙
        </span>
      ))}
    </div>
  );
}
